import { z } from 'zod';

import { IataSchema, NumericIdSchema } from './mice_refs';

// Folha (só importa `mice_refs`): `mice` e `mice_invites` dependem dela sem fechar ciclo.
// Horários e datas são SEMPRE do relógio local do aeroporto; nunca se converte fuso.

const HhMm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/** Regra de transfer do evento: janela de agrupamento em minutos; nulo = sem transfer. */
export const MiceTransferRuleSchema = z
  .object({
    groupMinutes: z.union([z.literal(30), z.literal(60), z.literal(90), z.literal(120)]),
  })
  .nullable();
export type MiceTransferRule = z.infer<typeof MiceTransferRuleSchema>;

export const MiceTransferDirectionSchema = z.enum(['outbound', 'return']);
export type MiceTransferDirection = z.infer<typeof MiceTransferDirectionSchema>;

/** De onde veio o voo da pessoa: voucher vivo, pedido, versão aprovada, só recomendado ou o
 *  que o convidado informou. */
export const MiceTransferSourceSchema = z.enum([
  'issued',
  'ordered',
  'approved',
  'recommended',
  'informed',
]);
export type MiceTransferSource = z.infer<typeof MiceTransferSourceSchema>;

/** Uma perna do transfer: ida = chegada final; volta = primeira partida. */
export const MiceTransferLegSchema = z.object({
  airport: IataSchema,
  date: z.iso.date(),
  time: HhMm,
  flight: z.string().nullable(),
});
export type MiceTransferLeg = z.infer<typeof MiceTransferLegSchema>;

export const MiceTransferPersonSchema = z.object({
  participantId: z.string(),
  name: z.string(),
  leg: MiceTransferLegSchema,
  source: MiceTransferSourceSchema,
  /** O organizador moveu a pessoa para este grupo (ou a deixou em grupo próprio). */
  adjusted: z.boolean(),
});
export type MiceTransferPerson = z.infer<typeof MiceTransferPersonSchema>;

export const MiceTransferGroupSchema = z.object({
  key: z.string(),
  airport: IataSchema,
  date: z.iso.date(),
  from: HhMm,
  to: HhMm,
  people: z.array(MiceTransferPersonSchema),
});
export type MiceTransferGroup = z.infer<typeof MiceTransferGroupSchema>;

/** O voo como o convite o mostra, companhia e número separados ("G32094" → "G3 2094"). Só
 *  exibição: o código guardado segue junto, porque é a chave do ajuste do organizador. Fora do
 *  formato, o texto como veio. */
export function transferFlightLabel(flight: string): string {
  const compact = flight.replaceAll(/\s+/g, '').toUpperCase();
  const match = /^([A-Z][A-Z\d]|\d[A-Z])(\d{1,5})$/.exec(compact);
  return match ? `${match[1] ?? ''} ${match[2] ?? ''}` : flight.trim();
}

/** A janela do grupo; o grupo de um horário só (início = fim) mostra só ele. */
export function transferWindowLabel({ from, to }: Pick<MiceTransferGroup, 'from' | 'to'>): string {
  return from === to ? from : `${from}–${to}`;
}

const MiceTransferNamedSchema = z.object({ participantId: z.string(), name: z.string() });
const MiceTransferNamedDirectionSchema = MiceTransferNamedSchema.extend({
  direction: MiceTransferDirectionSchema,
});

export const MiceTransferViewSchema = z.object({
  groupMinutes: MiceTransferRuleSchema.unwrap().shape.groupMinutes,
  outbound: z.array(MiceTransferGroupSchema),
  return: z.array(MiceTransferGroupSchema),
  /** Vão ao evento e ainda sem voo conhecido. */
  missing: z.array(MiceTransferNamedSchema),
  /** Fora da planilha por decisão do organizador. */
  excluded: z.array(MiceTransferNamedDirectionSchema),
  /** Ajuste caído porque o voo, a janela ou o grupo alvo mudou. */
  stale: z.array(MiceTransferNamedDirectionSchema),
});
export type MiceTransferView = z.infer<typeof MiceTransferViewSchema>;

/** Resposta das rotas de transfer do organizador. */
export const MiceTransferReadSchema = z.object({ transfer: MiceTransferViewSchema });
export type MiceTransferRead = z.infer<typeof MiceTransferReadSchema>;

/** Resumo na ficha do evento (`MiceProjectView.transfer`). */
export const MiceTransferSummarySchema = z.object({
  outboundGroups: z.number().int().min(0),
  returnGroups: z.number().int().min(0),
  missing: z.number().int().min(0),
});
export type MiceTransferSummary = z.infer<typeof MiceTransferSummarySchema>;

const DateRangeSchema = z.object({ min: z.iso.date(), max: z.iso.date() });

/** Passo de transfer do convite (quem vai sem necessidade de voo). */
export const MiceInviteTransferSchema = z.object({
  /** Nulo = ainda não respondeu. */
  needed: z.boolean().nullable(),
  outbound: MiceTransferLegSchema.nullable(),
  return: MiceTransferLegSchema.nullable(),
  /** Opções de aeroporto vindas da regra de chegada do evento. */
  airports: z.array(IataSchema),
  dateRange: z.object({ outbound: DateRangeSchema, return: DateRangeSchema }),
});
export type MiceInviteTransfer = z.infer<typeof MiceInviteTransferSchema>;

export const MiceInviteTransferSaveRequestSchema = z
  .object({
    travelerId: NumericIdSchema,
    needed: z.boolean(),
    outbound: MiceTransferLegSchema.nullable(),
    return: MiceTransferLegSchema.nullable(),
  })
  .refine((v) => !v.needed || (v.outbound !== null && v.return !== null), {
    message: 'needed requires both outbound and return legs',
  });
export type MiceInviteTransferSaveRequest = z.infer<typeof MiceInviteTransferSaveRequestSchema>;

export const MiceTransferAdjustActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('move'), groupKey: z.string() }),
  z.object({ type: z.literal('own') }),
  z.object({ type: z.literal('exclude') }),
  z.object({ type: z.literal('reset') }),
]);
export type MiceTransferAdjustAction = z.infer<typeof MiceTransferAdjustActionSchema>;

export const MiceTransferAdjustRequestSchema = z.object({
  organizerId: NumericIdSchema,
  participantId: z.string(),
  direction: MiceTransferDirectionSchema,
  action: MiceTransferAdjustActionSchema,
});
export type MiceTransferAdjustRequest = z.infer<typeof MiceTransferAdjustRequestSchema>;
